import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { toast } from 'sonner';

// Returns a base64 JPEG of the cropped region.
const cropToDataUrl = (src, area) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = Math.max(1, Math.round(area.width));
        const h = Math.max(1, Math.round(area.height));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = src;
  });

const ASPECT_PRESETS = [
  { label: 'Free', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
];

const ImageCropDialog = ({ open, src, onCancel, onSave }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(undefined);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_a, areaPixels) => setPixels(areaPixels), []);

  const handleSave = async () => {
    if (!src || !pixels) return;
    setSaving(true);
    try {
      const dataUrl = await cropToDataUrl(src, pixels);
      onSave(dataUrl);
    } catch (err) {
      console.error('Crop save failed:', err);
      toast.error('Could not save photo', {
        description: 'The image could not be processed. Please try a different file.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (next) => {
    if (!next) {
      onCancel();
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAspect(undefined);
      setPixels(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-fnl-purple text-white border-purple-900 max-w-xl"
        data-testid="image-crop-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">Crop your photo</DialogTitle>
        </DialogHeader>

        <div className="relative w-full h-80 bg-black rounded-lg overflow-hidden">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
              restrictPosition={false}
            />
          )}
        </div>

        <div className="mt-3 px-1">
          <div className="flex items-center gap-2 mb-3" data-testid="image-crop-aspect-row">
            <span className="text-xs text-purple-300 w-12">Aspect</span>
            {ASPECT_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => {
                  setAspect(p.value);
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                }}
                className={
                  aspect === p.value
                    ? 'px-3 py-1 rounded-full text-xs font-semibold bg-fnl-pink text-white'
                    : 'px-3 py-1 rounded-full text-xs font-medium bg-fnl-purple-light/40 text-purple-200 hover:bg-fnl-purple-light'
                }
                data-testid={`image-crop-aspect-${p.label.toLowerCase().replace(':','x')}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-purple-300 w-12">Zoom</span>
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.05}
              onValueChange={(v) => setZoom(v[0])}
              className="flex-1"
              data-testid="image-crop-zoom"
            />
            <span className="text-xs text-purple-300 w-10 text-right">{zoom.toFixed(2)}x</span>
          </div>
          <p className="text-[11px] text-purple-300 mt-2">
            Drag to position. Pick an aspect ratio or keep it free-form.
          </p>
        </div>

        <DialogFooter className="mt-2 gap-2 sm:gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="text-white hover:bg-white/10"
            data-testid="image-crop-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!pixels || saving}
            className="bg-fnl-pink hover:bg-fnl-pink text-white"
            data-testid="image-crop-save"
          >
            {saving ? 'Saving...' : 'Save photo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog;
